import type { Duration } from 'date-fns';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { DateFormat } from '../components/Timeline/TimelineFilterChip';
import { TimeScaleFilterLabel } from '../enums';
import type { TimeScaleFilter } from '../types';

type DateFormats = Record<keyof Omit<Duration, 'seconds'>, string>;

const defaultDateFormats: DateFormats = {
  minutes: 'H:mm',
  hours: 'H:mm',
  days: 'd',
  weeks: 'w',
  months: 'M',
  years: 'y',
};

export const scaleDefaultFilters: TimeScaleFilter[] = [
  {
    label: TimeScaleFilterLabel.ONE_HOUR,
    measureType: 'hours',
    measureScale: 1,
    dateFormat: defaultDateFormats.hours as DateFormat,
  },
  {
    label: TimeScaleFilterLabel.FOUR_HOURS,
    measureType: 'hours',
    measureScale: 4,
    dateFormat: defaultDateFormats.hours as DateFormat,
  },
  {
    label: TimeScaleFilterLabel.EIGHT_HOURS,
    measureType: 'hours',
    measureScale: 8,
    dateFormat: defaultDateFormats.hours as DateFormat,
  },
  {
    label: TimeScaleFilterLabel.ONE_MONTH,
    measureType: 'months',
    measureScale: 30 * 24,
    dateFormat: defaultDateFormats.months as DateFormat,
  },
];

export type TimelineStoreState = {
  filters: {
    current: TimeScaleFilter;
  };
};

export type TimelineStoreActions = {
  handleFilterChange: (filter: TimeScaleFilter) => void;
};

type TimelineStore = TimelineStoreState & TimelineStoreActions;
export const useTimelineStore = create(
  subscribeWithSelector(
    persist(
      immer<TimelineStore>((set) => {
        return {
          filters: {
            current: scaleDefaultFilters[0] as TimeScaleFilter,
          },
          handleFilterChange: (filter: TimeScaleFilter) =>
            set((state) => {
              state.filters.current = filter;
            }),
        };
      }),
      {
        name: 'gantt-timeline-store',
        version: 1,
        partialize: (state) => {
          return {
            filters: state.filters,
          };
        },
        storage: createJSONStorage(() => {
          return {
            setItem: localStorage.setItem.bind(localStorage),
            removeItem: localStorage.removeItem.bind(localStorage),
            getItem: localStorage.getItem.bind(localStorage),
          };
        }),
      },
    ),
  ),
);
